package com.schoolconnect.app.repository.reports

import com.schoolconnect.app.datasource.network.request.AttendanceReportStatus
import com.schoolconnect.app.datasource.network.response.ClassSectionResponse
import com.schoolconnect.app.datasource.network.response.ExamResponse
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.TeacherClassStatistics
import com.schoolconnect.app.datasource.network.response.TeacherStudentListItem
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.exam.ExamRepository
import com.schoolconnect.app.repository.mystudents.TeacherMyStudentsRepository
import com.schoolconnect.app.repository.student.StudentRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.util.scInfo

data class StaffReportClassOption(val id: String, val label: String)
data class StaffReportExamOption(val id: String, val label: String)
data class StaffReportStudentOption(val id: String, val label: String)
data class StaffReportStudentsPage(val items: List<StaffReportStudentOption>, val totalCount: Int)
data class StaffReportAttendanceStatusOption(val value: AttendanceReportStatus, val label: String)

object StaffReportsRepository {
    private const val TAG = "[REPORTS]"
    private const val STUDENT_PAGE_SIZE = PaginationPolicy.DEFAULT_PAGE_SIZE

    suspend fun loadClassOptions(
        isTeacher: Boolean,
        schoolId: String,
        activeAcademicYearId: String
    ): Result<List<StaffReportClassOption>> {
        scInfo("$TAG loadClassOptions start teacher=$isTeacher")
        val result = if (isTeacher) {
            TeacherMyStudentsRepository.loadDashboardStatistics().map { response ->
                response.assignedClasses.map { section ->
                    classOption(section)
                        ?: error("A teacher-assigned class is missing its identifier or display name.")
                }
            }
        } else {
            ClassSectionRepository.loadSchoolClassSectionsForAcademicYear(schoolId, activeAcademicYearId)
                .map { sections ->
                    sections.map { section ->
                        classOption(section)
                            ?: error("An active class section is missing its identifier or display name.")
                    }
                }
        }
        result.onSuccess { scInfo("$TAG loadClassOptions success count=${it.size}") }
            .onFailure { scInfo("$TAG loadClassOptions failure type=${it::class.simpleName}") }
        return result
    }

    suspend fun loadAttendanceStatusOptions(): Result<List<StaffReportAttendanceStatusOption>> {
        val snapshot = SystemLookupRepository.refresh(forceRemote = false).getOrElse { return Result.failure(it) }
        val category = snapshot.categories.firstOrNull { it.equals("attendance_status", ignoreCase = true) }
            ?: return Result.failure(IllegalStateException("The attendance-status lookup category is unavailable."))
        val items = SystemLookupRepository.loadCategoryItems(category).getOrElse { return Result.failure(it) }
        val options = items.mapNotNull { item ->
            val value = when (item.code?.trim()?.uppercase()) {
                "P" -> AttendanceReportStatus.PRESENT
                "A" -> AttendanceReportStatus.ABSENT
                "A-M" -> AttendanceReportStatus.ABSENT_MORNING
                "A-A" -> AttendanceReportStatus.ABSENT_AFTERNOON
                else -> null
            } ?: return@mapNotNull null
            val label = item.displayValue?.trim()?.takeIf(String::isNotBlank)
                ?: return Result.failure(IllegalStateException("An attendance-status lookup value has no display label."))
            StaffReportAttendanceStatusOption(value, label)
        }.distinctBy { it.value }
        if (options.isEmpty()) {
            return Result.failure(IllegalStateException("The attendance-status lookup has no values supported by the report API."))
        }
        return Result.success(options)
    }

    suspend fun loadExamOptions(classSectionIds: List<String>): Result<List<StaffReportExamOption>> {
        val ids = classSectionIds.map(String::trim).filter(String::isNotBlank).distinct()
        if (ids.isEmpty()) return Result.success(emptyList())
        val exams = ExamRepository.loadPage(ids, pageSize = 100).getOrElse { return Result.failure(it) }.items
        val published = exams.filter { it.published == true || it.status.equals("PUBLISHED", ignoreCase = true) }
        val malformed = published.firstOrNull { it.id.isNullOrBlank() || it.title.isNullOrBlank() }
        if (malformed != null) {
            return Result.failure(IllegalStateException("A published exam is missing its identifier or title."))
        }
        return Result.success(
            published.distinctBy { it.id }.map { StaffReportExamOption(it.id!!, it.title!!) }
                .sortedBy { it.label.lowercase() }
        )
    }

    suspend fun loadStudentsPage(
        isTeacher: Boolean,
        schoolId: String,
        classSectionId: String,
        query: String,
        pageIndex: Int
    ): Result<StaffReportStudentsPage> {
        val normalizedClassId = classSectionId.trim()
        if (normalizedClassId.isBlank()) {
            return Result.failure(IllegalArgumentException("Select a class before loading students."))
        }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        return if (isTeacher) {
            TeacherMyStudentsRepository.loadPage(
                query = query,
                classSectionId = normalizedClassId,
                pageIndex = safePageIndex,
                pageSize = STUDENT_PAGE_SIZE,
                sort = ServerSort("name", ServerSortOrder.ASC),
                forceRemote = true
            ).map { response ->
                val count = response.count
                    ?: error("The teacher student lookup response is missing its filtered count.")
                StaffReportStudentsPage(
                    response.students.map {
                        studentOption(it) ?: error("A teacher student lookup row is missing its identifier or name.")
                    },
                    count
                )
            }
        } else {
            StudentRepository.loadSchoolStudentsPage(
                schoolId = schoolId,
                query = query,
                classSectionId = normalizedClassId,
                pageIndex = safePageIndex,
                pageSize = STUDENT_PAGE_SIZE,
                sort = ServerSort("name", ServerSortOrder.ASC),
                forceRemote = true
            ).map { response ->
                val count = response.serverTotalCountOrNull()
                    ?: error("The student lookup response is missing its filtered count.")
                StaffReportStudentsPage(
                    response.map {
                        studentOption(it) ?: error("A student lookup row is missing its identifier or name.")
                    },
                    count
                )
            }
        }
    }

    private fun classOption(section: TeacherClassStatistics): StaffReportClassOption? {
        val id = section.classSectionId?.trim()?.takeIf(String::isNotBlank) ?: return null
        val className = section.className?.trim()?.takeIf(String::isNotBlank) ?: return null
        val sectionName = section.sectionName?.trim()?.takeIf(String::isNotBlank) ?: return null
        return StaffReportClassOption(id, "$className-$sectionName")
    }

    private fun classOption(section: ClassSectionResponse): StaffReportClassOption? {
        val id = section.id?.trim()?.takeIf(String::isNotBlank) ?: return null
        val className = section.className?.trim()?.takeIf(String::isNotBlank) ?: return null
        val sectionName = section.sectionName?.trim()?.takeIf(String::isNotBlank) ?: return null
        return StaffReportClassOption(id, "$className-$sectionName")
    }

    private fun studentOption(student: TeacherStudentListItem): StaffReportStudentOption? {
        val id = (student.studentId ?: student.id)?.trim()?.takeIf(String::isNotBlank) ?: return null
        val name = student.name?.trim()?.takeIf(String::isNotBlank) ?: return null
        return StaffReportStudentOption(id, name)
    }

    private fun studentOption(student: StudentListResponse): StaffReportStudentOption? {
        val id = student.id?.trim()?.takeIf(String::isNotBlank) ?: return null
        val name = student.name?.trim()?.takeIf(String::isNotBlank) ?: return null
        return StaffReportStudentOption(id, name)
    }
}
